/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Grid, PageGridContainer, Typography, Typography2, breakpoints, typography2 } from '@coursera/cds-core';
import { useVisibilityTracker } from '@coursera/event-pulse/react';

import TrackedDiv from 'bundles/page/components/TrackedDiv';
import Section from 'bundles/unified-description-page-common/components/shared/Section';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';

type Props = {
  title: string;
  anchorId?: string;
  valueProps?: string[];
  subtitle?: React.ReactNode;
  dataBox?: React.ReactNode;
  disclaimers?: string | null;
};

const styles = {
  titleContainer: () => css`
    padding-right: var(--cds-spacing-400);
    ${breakpoints.down('sm')} {
      padding-top: var(--cds-spacing-400);
      padding-right: 0;
    }
  `,
  valueProps: () => css`
    list-style: disc;
    margin-left: var(--cds-spacing-300);
    margin-top: var(--cds-spacing-200);

    ${typography2.bodyPrimary}
    li {
      margin-top: var(--cds-spacing-100);
      padding-left: var(--cds-spacing-50);
    }
  `,
  gridContainer: () => css`
    align-items: center;
    ${breakpoints.down('sm')} {
      align-items: unset;
      flex-direction: column-reverse;

      /* override "flex-wrap: wrap" applied by CDS to prevent content overlap */
      flex-wrap: nowrap;
    }
  `,
  disclaimers: () => css`
    margin-top: var(--cds-spacing-400);
    white-space: pre-wrap;
  `,
};

const CareerOutcomesModule = ({ title, subtitle, anchorId, valueProps, dataBox, disclaimers }: Props) => {
  const eventingProductData = useEventingProductData();

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_page_section',
    {
      pageSection: {
        sectionName: 'outcomes',
      },
      ...eventingProductData,
    }
  );

  return (
    <TrackedDiv
      trackingName="career_outcomes"
      trackClicks={false}
      requireFullyVisible={false}
      withVisibilityTracking={true}
    >
      <Section anchorId={anchorId} ref={visibilityTrackingRef}>
        <PageGridContainer>
          <Grid item container>
            <Grid item container xs={12} css={styles.gridContainer} justifyContent="space-between">
              <Grid item xs={12} md={6} css={styles.titleContainer}>
                <Typography variant="h1semibold" component="h2">
                  {title}
                </Typography>
                {subtitle}
                {valueProps && (
                  <ul css={styles.valueProps}>
                    {valueProps.map((valueProp, index) => {
                      // eslint-disable-next-line react/no-array-index-key
                      return <li key={index}>{valueProp}</li>;
                    })}
                  </ul>
                )}
                {disclaimers && (
                  <Typography2 component="p" variant="bodySecondary" color="supportText" css={styles.disclaimers}>
                    {disclaimers}
                  </Typography2>
                )}
              </Grid>
              <Grid item xs={12} md={6} lg={5}>
                {dataBox}
              </Grid>
            </Grid>
          </Grid>
        </PageGridContainer>
      </Section>
    </TrackedDiv>
  );
};

export default CareerOutcomesModule;
